import { Box, Drawer, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ReactNode } from "react";
import { useAppTheme } from "@/hooks/useAppTheme";

/**
 * Props for the SideDrawer component.
 */
interface SideDrawerProps {
  /**
   * Optional title displayed in the drawer header.
   */
  title?: string;

  /**
   * Whether the drawer is open.
   */
  open: boolean;

  /**
   * Callback function triggered when the drawer is closed.
   */
  onClose: () => void;

  /**
   * Width of the drawer in pixels. Defaults to 500.
   */
  width?: number;

  /**
   * Main content to render inside the drawer.
   */
  children: ReactNode;

  /**
   * If true, hides the side drawer handle button.
   * Useful when controlling drawer visibility externally.
   */
  hideHandle?: boolean;

  /**
   * Offset from the right edge in pixels. Used to stack multiple drawers
   * side-by-side (e.g., endorsement drawer offset by PDF drawer width).
   */
  offsetRight?: number;
}

/**
 * A right-anchored persistent drawer with optional title, close button,
 * and toggle handle for minimizing. Typically used for displaying
 * additional contextual content without navigating away.
 *
 * @param {SideDrawerProps} props - Props for the SideDrawer component.
 * @returns {JSX.Element} The rendered drawer component.
 */
export const SideDrawer = ({
  title,
  open,
  onClose,
  width = 500,
  children,
  hideHandle = false,
  offsetRight = 0,
}: SideDrawerProps): JSX.Element => {
  const { theme } = useAppTheme();

  return (
    <>
      {/* Main Drawer */}
      <Drawer
        variant="persistent"
        anchor="right"
        open={open}
        sx={{
          // Don't reserve width in document flow — parent manages margin-right
          width: 0,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            transition: "width 0.3s ease",
            ...(offsetRight > 0 && {
              right: offsetRight,
              transition: "right 0.3s ease, width 0.3s ease",
            }),
          },
        }}
      >
        {/* Header section with title and close button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 0.75,
            bgcolor: theme.palette.secondary.main,
            color: "background.paper",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {title && <Typography variant="h3">{title}</Typography>}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "background.paper",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Drawer content */}
        <Box sx={{ flex: 1, overflow: "auto", px: 3, pt: 2 }}>{children}</Box>
      </Drawer>

      {/* Optional toggle handle for closing the drawer */}
      {!hideHandle && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            right: open ? width + offsetRight : offsetRight,
            transform: "translateY(-50%)",
            zIndex: 1300,
            transition: "right 0.3s",
          }}
        >
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              width: 28,
              height: 64,
              borderRadius: "4px 0 0 4px",
              bgcolor: "background.paper",
              border: 1,
              borderRight: 0,
              borderColor: "divider",
              color: "text.secondary",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            {open ? (
              <ExpandLessIcon sx={{ transform: "rotate(90deg)" }} fontSize="small" />
            ) : (
              <ExpandMoreIcon sx={{ transform: "rotate(90deg)" }} fontSize="small" />
            )}
          </IconButton>
        </Box>
      )}
    </>
  );
};
